import { feelingItem } from '../../../packages/domain/src/44-feeling-items.mjs';
import { feelingExperience } from '../../../packages/domain/src/46-feeling-experiences.mjs';
import {
  splitPostText,
  POST_TEXT_LIMIT,
} from '../../../packages/domain/src/23-post-continuations.mjs';

/** Preserve exact author text in the DOM while trailing blank lines consume no
 * visual space. Only whitespace is hidden; stored continuation text is unchanged. */
export function postParagraph(ctx, className, text) {
  const visible = text.trimEnd();
  const paragraph = ctx.e('p', className, visible);
  if (visible.length !== text.length) {
    const tail = ctx.e('span', '', text.slice(visible.length));
    tail.hidden = true;
    paragraph.append(tail);
  }
  return paragraph;
}

export function editorialSourceNote(ctx, post) {
  const body = ctx.append(
    ctx.e('div', 'comment-body'),
    ctx.e('strong', '', ctx.uiText('Editorial source note')),
  );
  for (const [label, href, field] of [
    [post.sourceTitle || 'Source', post.sourceURL],
    ...(post.editorialSources ?? [])
      .filter((source) => source.url !== post.sourceURL)
      .map((source) => [source.label, source.url]),
    [post.imageCredit, post.imageSourceURL, 'imageCredit'],
    [post.imageLicense, post.imageLicenseURL, 'imageLicense'],
  ]) {
    if (!label) continue;
    const line = ctx.e('p');
    if (field) line.dataset.imageField = field;
    if (href && /^https:\/\//i.test(href)) {
      const source = ctx.link(label, href);
      source.target = '_blank';
      source.rel = 'noopener noreferrer';
      line.append(source);
    } else line.append(ctx.e('span', '', label));
    body.append(line);
  }
  if (post.imageChanges) {
    // Published source notes describe artwork, not the historical review workflow.
    const changes = ctx.e(
      'p',
      '',
      post.imageChanges.replace(/\s*Pending independent acceptance\.$/, ''),
    );
    changes.dataset.imageField = 'imageChanges';
    body.append(changes);
  }
  const note = ctx.append(ctx.e('div', 'comment editorial-source-note'), body);
  note.dataset.commentId = `editorial-source-${post.id}`;
  return note;
}

/** Native collapsed provenance keeps story artwork accurately attributed without
 * repeating instructions or superseded poster credits beside the active scene. */
export function postSourceNote(ctx, post) {
  const experience = feelingExperience(post);
  const note = editorialSourceNote(
    ctx,
    experience ? { ...post, imageCredit: '', imageLicense: '', imageChanges: '' } : post,
  );
  if (experience) {
    for (const stage of experience.stages) {
      const art = stage.artwork;
      note.append(ctx.e('p', '', `${stage.title}: ${art.credit}. ${art.license}`));
    }
  }
  return feelingItem(post.feelingItem)
    ? ctx.append(
        ctx.e('details', 'post-editorial-disclosure'),
        ctx.e('summary', '', 'Editorial source note'),
        note,
      )
    : note;
}

/** Continuations are ordered author text, deliberately separate from reply counts and reactions. */
export function appendContinuations(ctx, region, post) {
  for (const part of post.authorContinuations ?? []) {
    const author = ctx.who(post.authorId);
    const body = ctx.append(
      ctx.e('div', 'comment-body'),
      ctx.e(
        'strong',
        '',
        `${ctx.uiText('Continued by')} ${author.name} · ${part.index}/${part.total}`,
      ),
      postParagraph(ctx, 'post-continuation-text', part.text),
    );
    const node = ctx.append(ctx.e('div', 'comment author-continuation'), body);
    node.dataset.commentId = part.id;
    region.append(node);
  }
}

/** The explicit action remains discoverable even when nobody has replied. */
export function readFullPost(ctx, post, open) {
  const button = ctx.button('Read full post', open, 'text-button read-full-post');
  button.dataset.focusKey = `post-${post.id}-read-full`;
  button.setAttribute(
    'aria-label',
    post.authorReadings?.length
      ? `${ctx.uiText('Read full post')} · ${ctx.uiText(post.authorReadings.some((note) => note.kind === 'starter_editorial') ? 'Further reading' : 'Lesson notes')}`
      : `${ctx.uiText('Read full post')} · ${post.authorContinuations?.length ?? 0} ${ctx.uiText('author continuations')}`,
  );
  return button;
}

/** Preview uses the same split policy as the server; the storage cap remains explicit. */
export function continuationPreview(ctx, input, { story = false } = {}) {
  const note = ctx.e('p', 'subtle continuation-preview');
  note.setAttribute('role', 'status');
  const update = () => {
    const count = story ? 0 : Math.max(0, splitPostText(input.value).length - 1);
    note.textContent = `${input.value.length}/${POST_TEXT_LIMIT} ${ctx.uiText('characters')}. ${count ? `${count} ${ctx.uiText('automatic author continuations will appear before replies.')}` : ctx.uiText('Text fits in one post.')}`;
  };
  input.addEventListener('input', update);
  update();
  return note;
}

/** Patch only image provenance labels after authorized media-only recovery. Keep
 * existing source links, reply inputs and focused elements mounted. */
export function refreshEditorialImageNote(ctx, card, post) {
  for (const note of card.querySelectorAll('.editorial-source-note')) {
    for (const field of ['imageCredit', 'imageLicense', 'imageChanges']) {
      let line = note.querySelector(`[data-image-field="${field}"]`);
      if (!line && post[field]) {
        line = ctx.e('p');
        line.dataset.imageField = field;
        note.querySelector('.comment-body').append(line);
      }
      if (line) (line.firstElementChild ?? line).textContent = post[field] ?? '';
    }
  }
}

/** Proposed export within UI16, called after appendContinuations and before member replies.
 * Existing text-node/whitespace and source-link renderers preserve content without innerHTML.
 * No reaction/count/reply widgets are attached to notes. Translation keys require root review. */
export function appendAuthorReadings(ctx, region, post) {
  region.dataset.authorReadingsRegion = post.id;
  for (const reading of post.authorReadings ?? []) {
    const author = ctx.who(reading.authorId),
      label = reading.kind === 'starter_editorial' ? 'Further reading' : 'Lesson notes',
      group = ctx.e('section', 'author-lesson-notes');
    group.dataset.readingId = reading.id;
    group.setAttribute('aria-label', `${ctx.uiText(label)} · ${author.name}`);
    for (const part of reading.parts) {
      const body = ctx.append(
        ctx.e('div', 'comment-body'),
        ctx.e('strong', '', `${ctx.uiText(label)} · ${author.name} · ${part.index}/${part.total}`),
        postParagraph(ctx, 'post-continuation-text', part.text),
      );
      const node = ctx.append(ctx.e('div', 'author-continuation author-lesson-note-part'), body);
      node.dataset.noteId = part.id;
      group.append(node);
    }
    group.append(
      editorialSourceNote(ctx, {
        id: reading.id,
        sourceURL: reading.sourceURLs[0],
        sourceTitle: 'Source',
        editorialSources: reading.sourceURLs.slice(1).map((url) => ({ label: 'Source', url })),
      }),
    );
    region.append(group);
  }
}

/** Reconcile notes only in existing reading regions. Retain unchanged sections/focused
 * source links and every member composer; removed notes never survive accepted hydration. */
export function refreshAuthorReadings(ctx, card, post) {
  for (const region of card.querySelectorAll('[data-author-readings-region]')) {
    const old = [...region.children].filter((node) =>
      node.classList.contains('author-lesson-notes'),
    );
    const next = ctx.e('div');
    appendAuthorReadings(ctx, next, post);
    const ordered = [...next.children].map(
      (node) =>
        old.find((previous) => previous.dataset.readingId === node.dataset.readingId) ?? node,
    );
    const active = document.activeElement;
    const retainedFocus = old.some((node) => ordered.includes(node) && node.contains(active));
    const focused = old.some(
      (node) => !ordered.includes(node) && node.contains(document.activeElement),
    );
    for (const node of old) if (!ordered.includes(node)) node.remove();
    const anchor =
      [...region.children].find(
        (node) =>
          !node.classList.contains('author-lesson-notes') &&
          !node.classList.contains('author-continuation'),
      ) ?? null;
    for (const node of ordered) region.insertBefore(node, anchor);
    if (retainedFocus && document.activeElement !== active) active.focus({ preventScroll: true });
    if (focused) {
      region.setAttribute('tabindex', '-1');
      region.focus({ preventScroll: true });
    }
  }
}

/** Both original continuations and separately authorized lesson notes occupy the same
 * reading entry point before member replies; neither becomes a discussion event. */
export function appendCourseReadings(ctx, region, post) {
  appendContinuations(ctx, region, post);
  appendAuthorReadings(ctx, region, post);
}

/** One canonical disclosure condition covers original overflow and publisher notes. */
export function appendFullPostAction(ctx, card, post, expanded, open) {
  if (!expanded && (post.authorContinuations?.length || post.authorReadings?.length))
    card.append(readFullPost(ctx, post, open));
}

/** Reconcile the canonical disclosure itself on retained cards, without redrawing media or
 * member composers. Fresh presence/options and event closure are authoritative. */
export function reconcileFullPostAction(card, fresh) {
  const old = card.querySelector('.read-full-post'),
    next = fresh.querySelector('.read-full-post');
  const focused = Boolean(old && old.contains(document.activeElement));
  if (old && next) {
    old.parentNode.insertBefore(next, old);
    old.remove();
  } else if (old) old.remove();
  else if (next) {
    const overlay = card.querySelector('.visual-overlay');
    if (overlay) overlay.append(next);
    else card.insertBefore(next, card.children[[...fresh.children].indexOf(next)] ?? null);
  }
  if (focused) {
    const fallback = next ?? card.querySelector('.post-actions button') ?? card;
    if (fallback === card) card.setAttribute('tabindex', '-1');
    fallback.focus({ preventScroll: true });
  }
}
